import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectOffice } from '~/actions';
import { Autocomplete, Button, Column, ConfirmPopup, Row, Text } from '~/commonComponents';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks/useAppSelector';
import { Colors } from '~/themes/colors';

interface IProps {
  description: string;
  targetUrl: string;
  text: string;
  isDisabled?: boolean;
  onTrackEvent?: () => void;
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
}

export const OfficeAuthorizedButton = ({
  description,
  targetUrl,
  text,
  isDisabled = false,
  onTrackEvent,
  style,
  textStyle,
}: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const offices = useAppSelector((state) => state.user.offices);

  const [isLoading, setLoading] = useState(false);
  const [isShowPopup, setShowPopup] = useState(false);
  const [value, setValue] = useState(currentOffice?.id);

  const isUseDSOStructure = useRef(shouldUseDsoStructure()).current;

  const handleSelectOffice = () => {
    if (!value) {
      return;
    }

    setLoading(true);
    dispatch(selectOffice({ officeId: value, redirectUrl: targetUrl }));
  };

  const handleClick = () => {
    if (isUseDSOStructure && !currentOffice) {
      setShowPopup(true);
      return;
    }

    navigate(targetUrl);
    onTrackEvent?.();
  };

  const handleClosePopup = () => {
    setValue(currentOffice?.id);
    setShowPopup(false);
  };

  useEffect(() => {
    setValue(currentOffice?.id);
  }, [currentOffice?.id]);

  return (
    <>
      <Button
        dataCy={text === 'Post A Shift' ? 'post-shift-button' : undefined}
        disabled={isDisabled}
        text={text}
        style={{ width: 206, marginTop: 0, ...style }}
        textStyle={textStyle}
        onClick={handleClick}
      />
      {isShowPopup && (
        // @ts-expect-error ConfirmPopup component still written in JavaScript
        <ConfirmPopup
          title="Select Office"
          rightButtonAction={handleSelectOffice}
          leftButtonAction={handleClosePopup}
          rightButtonText="Select Office"
          leftButtonText="Go Back"
          titleStyle={{
            fontSize: 18,
          }}
          leftButtonDisabled={isLoading}
          rightButtonDisabled={isLoading || !value}
          rightButtonLoading={isLoading}
        >
          <Column alignItems="center">
            <Text textAlign="center" color={Colors.neutral_500} width="85%">
              {description}
            </Text>
            <Row
              marginTop={20}
              marginBottom={48}
              alignItems="center"
              justifyContent="center"
              gap={16}
            >
              <Text bold>Office Name:</Text>
              <Autocomplete
                options={offices.map((it) => ({
                  label: it.office_name,
                  value: it.id,
                }))}
                value={value}
                onChange={({ value }) => {
                  setValue(value);
                }}
              />
            </Row>
          </Column>
        </ConfirmPopup>
      )}
    </>
  );
};
